import { handleActions } from 'redux-actions';
import { DevicesLoadSuccessAction } from 'actions/deviceActions';
import { CellState, CellIndex, LayoutSize, zoomLevels, isValidLayoutSize } from 'types';
import {
    SetLayoutAction,
    FilterDevicesAction,
    SetDateAction,
    AssignStreamAction,
    ActivateCellAction,
    VideoConnectRequestAction, VideoConnectSuccessAction, VideoConnectFailureAction,
    FrameRequestAction, FrameSuccessAction, FrameFailureAction,
    SetZoomAction, SetScrollAction, SetSyncAction,
    SetTimelineLabelWidthAction,
    SetStreamPositionAction,
    DisplayVideoErrorAction, DismissVideoErrorAction,
    RestoreLayoutAction,
    VideoDisconnectedAction,
    VideoPlayAction,
    SeekAction,
    VideoPauseAction,
    VideoMuteAction,
    VideoUnmuteAction,
} from 'actions/monitoringActions';
import { TimelineSuccessAction, TimelineDataReceivedAction } from 'actions/timelineActions';
import { LogoutAction } from 'actions/shellActions';
import { splitStreamId } from 'util/streamId';
import * as moment from 'moment';

export type State = Readonly<{
    size: LayoutSize;
    date: Date;
    cells: ReadonlyArray<CellState>;
    activeCellIndex: CellIndex;
    filterText: string;
    labelWidth: number;
}>;

export const emptyCell = Object.freeze<CellState>({
    streamId: null,
    playbackSpeed: 1,
    connecting: false,
    connected: false,
    seeking: false,
    frame: null,
    error: null,
    timeline: null,
    minutesPerSegment: 30,
    sync: true,
    scroll: 1,
    live: true,
    mute: true,
    playing: true, // Start with video active by default
    streamPosition: 0,
    waitingForDisconnectTime: false,
});

export const emptyChangeImageCell = Object.freeze<CellState>({
    ...emptyCell,
    sync: false, // Change Image cell doesn't sync to anything
    live: false, // Starts out with still frames
    playing: false, // Change Image cell start with still frames
    mute: true,
});


export const initialState = Object.freeze<State>({
    size: 1, // The layout size, which is not the same as the number of cells
    date: new Date(new Date().setUTCHours(0, 0, 0, 0)),
    cells: [ emptyChangeImageCell, emptyCell ], // cell 0 is for Change Image screen; cell 1 is the first monitoring cell
    activeCellIndex: 1,
    filterText: '',
    labelWidth: 60,
});

export type ReducerAction =
    SetLayoutAction
    | FilterDevicesAction
    | SetDateAction
    | AssignStreamAction
    | ActivateCellAction
    | VideoPlayAction
    | VideoConnectRequestAction
    | VideoConnectSuccessAction
    | VideoConnectFailureAction
    | VideoDisconnectedAction
    | SeekAction
    | FrameRequestAction
    | FrameSuccessAction
    | FrameFailureAction
    | SetTimelineLabelWidthAction
    | SetScrollAction
    | SetZoomAction
    | SetSyncAction
    | SetStreamPositionAction
    | DisplayVideoErrorAction
    | DismissVideoErrorAction
    // Device Actions
    | DevicesLoadSuccessAction
    // Timeline Actions
    | TimelineSuccessAction
    | TimelineDataReceivedAction
    // Session Actions
    | LogoutAction
;
export type Reducer = (state: State, action: ReducerAction) => State;

// Helper method to handle tricky update of cell state
const updateCell = (state: Readonly<State>, cellIndex: CellIndex, fn: (cell: Readonly<CellState>) => CellState): State => {
    // No change if index is invalid
    if (cellIndex < 0 || cellIndex > state.size) {
        return state;
    }

    // Important: perform immutable update of cells array
    const { cells } = state;
    return {
        ...state,
        cells: [
            ...cells.slice(0, cellIndex),
            fn(cells[cellIndex]),
            ...cells.slice(cellIndex + 1),
        ],
    };
};

// Helper method to handle tricky update of all cell states
const updateEachCell = (state: Readonly<State>, fn: (cell: Readonly<CellState>, cellIndex: CellIndex) => CellState): State => {
    const oldCells = state.cells || initialState.cells;
    let changed = false;
    const cells = oldCells.map((oldCell, cellIndex: CellIndex) => {
        // Important: perform immutable update of cells array
        const newCell = fn(oldCell, cellIndex);
        if (newCell !== oldCell) {
            changed = true;
        }
        return newCell;
    });

    // Avoid changing object references if nothing changed
    if (!changed) {
        return state;
    }

    return {
        ...state,
        cells,
    };
};

export const monitoringReducer: Reducer = handleActions({
    SET_LAYOUT(state: State, action: SetLayoutAction) {
        const size = action.payload;
        if (!isValidLayoutSize(size)) {
            return state;
        }
        const numCells = size + 1; // +1 for the Change Image screen
        const cells = (state.cells || []).slice(0, numCells);
        for (let i = cells.length; i < numCells; i++) {
            cells.push(emptyCell); // TODO: when creating an empty cell, minutesPerSegment should be set to current syncZoom's MPS value
        }
        return {
            ...state,
            size,
            cells,
            activeCellIndex: state.activeCellIndex >= numCells ? 1 : state.activeCellIndex,
        };
    },

    RESTORE_LAYOUT(state: State, action: RestoreLayoutAction) {
        const { durableCells, activeCellIndex } = action.payload;
        if (!durableCells || !isValidLayoutSize(durableCells.length)) {
            return state;
        }
        const cells = [ emptyChangeImageCell ];
        for (let i = 0; i < durableCells.length; i++) {
            const durableCell = durableCells[i] || {};
            const { live, streamPosition } = durableCell;
            cells.push({
                ...emptyCell,
                ...durableCell,
                streamPosition: live ? 0 : streamPosition,
            });
        }
        return {
            ...state,
            size: durableCells.length,
            cells,
            activeCellIndex: (activeCellIndex < 1 || activeCellIndex > durableCells.length) ? 1 : activeCellIndex,
        };
    },

    FILTER_DEVICES(state: State, action: FilterDevicesAction) {
        return {
            ...state,
            filterText: action.payload,
        };
    },

    SET_DATE(state: State, action: SetDateAction) {
        const date = action.payload.date;

        state = updateEachCell(state, cell => {
            if (!cell.streamPosition) {
                return cell;
            }
            const m = moment(cell.streamPosition);
            m.year(date.getFullYear());
            m.month(date.getMonth());
            m.date(date.getDate());
            return {
                ...cell,
                streamPosition: m.valueOf(),
            };
        });

        return {
            ...state,
            date,
        };
    },

    ASSIGN_STREAM(state: State, action: AssignStreamAction) {
        const { streamId, cellIndex, paused } = action.payload;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            playing:
                cellIndex === 0         // is this the Change Image cell?
                    ? false             // if so, always start with a still image
                    : cell.streamId     // otherwise, check to see if the cell was previously occupied
                        ? cell.playing  // preserve the playing state of the previous camera
                        : true,         // otherwise, start with video for the new camera
            timeline: null,
            connected: false,
            connecting: false,
            seeking: false,
            frame: null,
            error: null,
            streamId,
            live: typeof paused === 'undefined' ? cell.live : !paused,
        }));
    },

    ACTIVATE_CELL(state: State, action: ActivateCellAction) {
        const cellIndex = action.payload;
        if (cellIndex < 1 || cellIndex > state.size) {
            return state;
        }
        return {
            ...state,
            activeCellIndex: cellIndex,
        };
    },

    VIDEO_CONNECT_REQUEST(state: State, action: VideoConnectRequestAction) {
        const cellIndex = action.payload;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            connecting: true,
            error: null,
        }));
    },
    
    VIDEO_CONNECT_SUCCESS(state: State, action: VideoConnectSuccessAction) {
        const { cellIndex, utcTime, live } = action.meta;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            connecting: false,
            connected: true,
            streamPosition: utcTime,
            live,
            error: null,
            frame: null,
        }));
    },
    
    VIDEO_CONNECT_FAILURE(state: State, action: VideoConnectFailureAction) {
        const { cellIndex } = action.meta;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            connecting: false,
            connected: false,
            playing: false,
            error: action.payload,
        }));
    },

    VIDEO_PAUSE(state: State, action: VideoPauseAction) {
        const cellIndex = action.payload;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            playing: false,
            live: false,
            waitingForDisconnectTime: true,
        }));
    },

    VIDEO_MUTE(state: State, action: VideoMuteAction) {
        const cellIndex = action.payload;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            mute: true,
        }));
    },

    VIDEO_UNMUTE(state: State, action: VideoUnmuteAction) {
        const cellIndex = action.payload;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            mute: false,
        }));
    },

    SEEK(state: State, action: SeekAction) {
        const { cellIndex } = action.payload;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            playing: false,
            live: false,
        }));
    },

    FRAME_REQUEST(state: State, action: FrameRequestAction) {
        const cellIndex = action.payload;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            seeking: true,
            error: null,
        }));
    },
    
    FRAME_SUCCESS(state: State, action: FrameSuccessAction) {
        const frame = action.payload;
        const { cellIndex, utcTime } = action.meta;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            frame,
            seeking: false,
            streamPosition: utcTime,
            error: null,
        }));
    },
    
    FRAME_FAILURE(state: State, action: FrameFailureAction) {
        const { cellIndex } = action.meta;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            seeking: false,
            error: action.payload,
        }));
    },

    VIDEO_PLAY(state: State, action: VideoPlayAction) {
        const { cellIndex, live } = action.payload;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            playing: true,
            live,
        }));
    },
    
    DISPLAY_VIDEO_ERROR(state: State, action: DisplayVideoErrorAction) {
        const { cellIndex } = action.meta;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            error: action.payload,
        }));
    },

    DISMISS_VIDEO_ERROR(state: State, action: DismissVideoErrorAction) {
        const { cellIndex } = action.meta;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            error: null,
        }));
    },

    DEVICES_LOAD_SUCCESS(state: State, action: DevicesLoadSuccessAction) {
        const { cameras = {} } = action.payload;
        return updateEachCell(state, cell => {
            if (!cell.streamId) {
                return cell;
            }

            const { deviceId } = splitStreamId(cell.streamId);
            const cameraIds = Object.keys(cameras);

            if (cameraIds.indexOf(deviceId) === -1) {
                return emptyCell;
            }

            return cell;
        });
    },

    VIDEO_DISCONNECTED(state: State, action: VideoDisconnectedAction) {
        const { cellIndex, utcTime } = action.payload;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            connected: false,
            connecting: false,
            playing: false,
            streamPosition: cell.waitingForDisconnectTime ? utcTime : cell.streamPosition,
            waitingForDisconnectTime: false,
        }));
    },

    SET_ZOOM(state: State, action: SetZoomAction) {
        const { cellIndex, minutesPerSegment } = action.payload;
        const { sync } = state.cells[cellIndex];
        if (zoomLevels.indexOf(minutesPerSegment) < 0) {
            throw new Error(`Invalid zoom level of ${minutesPerSegment}`);
        }
        return state.cells.reduce((s, cell, i) => {
            if (sync && s.cells[i].sync || i === cellIndex) {
                return updateCell(s, i as CellIndex, cell => ({
                    ...cell,
                    minutesPerSegment,
                }));
            }
            return s;
        }, state);
    },

    SET_SCROLL(state: State, action: SetScrollAction) {
        const { cellIndex, scroll } = action.payload;
        const { sync } = state.cells[cellIndex];
        if (scroll < 0 || scroll > 1) {
            throw new Error(`Invalid scroll level of ${scroll}`);
        }
        return state.cells.reduce((s, cell, i) => {
            if (sync && s.cells[i].sync || i === cellIndex) {
                return updateCell(s, i as CellIndex, cell => ({
                    ...cell,
                    scroll,
                }));
            }
            return s;
        }, state);
    },

    SET_SYNC(state: State, action: SetSyncAction) {
        // TODO: when enabling sync, should update all synced cells to have same minutesPerSegment and scroll as this cell
        const { cellIndex, sync } = action.payload;
        if (cellIndex === 0) {
            return state; // Not allowed to change sync of the Change Image cell
        }
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            sync,
        }));
    },

    SET_TIMELINE_LABEL_WIDTH(state: State, action: SetTimelineLabelWidthAction) {
        return {
            ...state,
            labelWidth: action.payload,
        };
    },

    SET_STREAM_POSITION(state: State, action: SetStreamPositionAction) {
        const { cellIndex } = action.meta;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            streamPosition: action.payload,
        }));
    },


    //
    //  Timeline Actions
    //
    TIMELINE_SUCCESS(state: State, action: TimelineSuccessAction) {
        const { cellIndex } = action.meta;
        return updateCell(state, cellIndex, cell => ({
            ...cell,
            timeline: action.payload,
        }));
    },

    TIMELINE_DATA_RECEIVED(state: State, action: TimelineDataReceivedAction) {
        const { deviceId } = action.meta;
        const messages = action.payload;
        if (!messages.length) {
            return state;
        }

        // Messages are sent in chronological order. Take the time of the last message
        const { t } = messages[messages.length - 1];

        return updateEachCell(state, (cell, cellIndex) => {
            // Don't update stream position for cells that are paused, showing historical video, or are vacant
            if (!cell.live || !cell.streamId) {
                return cell;
            }
            // Don't update stream position for cells loaded with a different device
            const streamId = splitStreamId(cell.streamId);
            if (deviceId !== streamId.deviceId) {
                return cell;
            }
            // Update the stream position to the time of the latest message received (or current time if that's more up-to-date)
            const streamPosition = Math.max(cell.streamPosition, t);
            return {
                ...cell,
                streamPosition,
            };
        });
    },
    

    //
    //  Shell Actions
    //
    LOGOUT(state: State, action: LogoutAction) {
        return {
            ...state,
            size: 1,
            cells: initialState.cells,
            activeCellIndex: 1,
            filterText: '',
        };
    },

// tslint:disable-next-line:no-any
} as any, initialState) as any;


// WEBPACK FOOTER //
// ./src/reducers/monitoringReducer.ts